import React, { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { useQueryClient } from '@tanstack/react-query'
import { RootState, AppDispatch } from '../store'
import { clearUser } from '../store/authSlice'
import { fetchInvitations, addInvitation } from '../store/invitationsSlice'
import authService from '../services/authService'
import { webSocketService } from '../services/websocket'
import Logo from './Logo'
import LanguageSelector from './LanguageSelector'
import { useTheme } from '../hooks/use-theme'
import { useTranslation } from '../hooks/use-i18n'
import { isAdminUser } from '../utils/admin'

const Navigation: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const queryClient = useQueryClient()
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth)
  const { unreadCount } = useSelector((state: RootState) => state.invitations)
  const { theme, toggleTheme } = useTheme()
  const { t } = useTranslation()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchInvitations())
    }
  }, [dispatch, isAuthenticated])

  // Real-time invitations and rooms: connect when logged in and subscribe to events
  useEffect(() => {
    const userId = user?._id
    if (!isAuthenticated || !userId) return

    webSocketService.connectForNotifications(userId)

    const onInvitationNew = (payload: any) => {
      if (payload && payload._id) dispatch(addInvitation(payload))
    }
    const onInvitationUpdated = () => {
      dispatch(fetchInvitations())
    }
    const onRoomsRefresh = () => {
      queryClient.invalidateQueries({ queryKey: ['user-rooms'] })
    }

    webSocketService.on('invitation:new', onInvitationNew)
    webSocketService.on('invitation:updated', onInvitationUpdated)
    webSocketService.on('rooms:refresh', onRoomsRefresh)

    return () => {
      webSocketService.off('invitation:new')
      webSocketService.off('invitation:updated')
      webSocketService.off('rooms:refresh')
    }
  }, [isAuthenticated, user?._id, dispatch, queryClient])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false)
      }
    }
    if (isMobileMenuOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isMobileMenuOpen])

  const handleLogout = () => {
    authService.logout()
    dispatch(clearUser())
    webSocketService.disconnect()
    navigate('/')
    setIsMobileMenuOpen(false)
  }

  // Landing uses its own navbar; signed-in `/` shows Dashboard with this bar
  if (location.pathname === '/' && !isAuthenticated) {
    return null
  }

  const linkClass = (path: string) =>
    `text-small lg:text-nav font-medium transition-colors ${location.pathname === path ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`

  const dashboardActive = location.pathname === '/' || location.pathname === '/dashboard'
  const dashboardLinkClass = `text-small lg:text-nav font-medium transition-colors ${dashboardActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`
  const isAdmin = isAdminUser(user)
  const adminActive = location.pathname.startsWith('/admin')
  const adminLinkClass = `text-small lg:text-nav font-medium transition-colors ${adminActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`

  return (
    <nav ref={menuRef} className="fixed top-0 left-0 right-0 z-50 glass navbar-bar w-full">
      <div className="mx-auto flex items-center justify-between h-12 sm:h-14 md:h-14 lg:h-16 px-3 xs:px-4 sm:px-4 lg:px-6 w-full max-w-full lg:max-w-5xl xl:max-w-6xl 2xl:max-w-7xl">
        <Link to="/" className="flex items-center shrink-0" aria-label="Break Room Cup">
          <Logo size="sm" />
        </Link>

        {/* Desktop - nav links aligned right (breakroom-champions style) */}
        <div className="hidden md:flex items-center gap-3 md:gap-4 lg:gap-6 xl:gap-8">
          {isAuthenticated ? (
            <>
              <Link to="/dashboard" className={dashboardLinkClass}>{t('nav.dashboard')}</Link>
              <Link to="/news" className={linkClass('/news')}>{t('nav.news')}</Link>
              <Link to="/invitations" className={`relative ${linkClass('/invitations')}`}>
                {t('nav.invitations')}
                {unreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 md:-top-2 md:-right-2 bg-destructive text-destructive-foreground text-[9px] md:text-[10px] font-bold rounded-full h-3.5 w-3.5 md:h-4 md:w-4 flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Link>
              <Link to="/account" className={linkClass('/account')}>{t('nav.account')}</Link>
              <Link to="/payments" className={linkClass('/payments')}>{t('nav.payments')}</Link>
              {isAdmin && (
                <Link to="/admin" className={adminLinkClass}>Admin</Link>
              )}
              <button onClick={toggleTheme} className="p-1.5 md:p-2 rounded-lg hover:bg-muted/30 text-muted-foreground hover:text-foreground" aria-label="Toggle theme">
                {theme === 'dark' ? <svg className="w-4 h-4 md:w-[18px] md:h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg> : <svg className="w-4 h-4 md:w-[18px] md:h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>}
              </button>
              <LanguageSelector />
              <span className="text-small lg:text-nav text-muted-foreground whitespace-nowrap hidden xl:inline">{t('nav.welcome', { name: user?.name ?? '' })}</span>
              <button
                onClick={handleLogout}
                className="text-small lg:text-nav text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 whitespace-nowrap"
              >
                {t('nav.signOut')}
              </button>
            </>
          ) : (
            <>
              <Link to="/news" className={linkClass('/news')}>{t('nav.news')}</Link>
              <Link to="/company-registration" className={linkClass('/company-registration')}>{t('nav.companies')}</Link>
              <Link to="/arena" className={linkClass('/arena')}>{t('nav.arena')}</Link>
              <Link to="/golf" className={linkClass('/golf')}>{t('nav.golf')}</Link>
              <Link to="/showcase" className={linkClass('/showcase')}>{t('nav.predictions')}</Link>
              <Link to="/about-us" className={linkClass('/about-us')}>{t('nav.about')}</Link>
              <button
                onClick={toggleTheme}
                className="p-1.5 md:p-2 rounded-lg hover:bg-muted/30 text-muted-foreground hover:text-foreground transition-all"
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? (
                  <svg className="w-4 h-4 md:w-[18px] md:h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                ) : (
                  <svg className="w-4 h-4 md:w-[18px] md:h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
                )}
              </button>
              <LanguageSelector />
              <Link to="/login" className="px-3 py-1.5 md:px-4 md:py-2 lg:px-5 lg:py-2 rounded-lg bg-primary text-primary-foreground text-small lg:text-nav font-semibold hover:opacity-90 transition-opacity">
                {t('nav.signIn')}
              </Link>
            </>
          )}
        </div>

        <div className="md:hidden flex items-center gap-2">
          <LanguageSelector />
          <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-muted/30 text-muted-foreground" aria-label="Toggle theme">
            {theme === 'dark' ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
            )}
          </button>
          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-foreground p-2" aria-label="Toggle menu">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMobileMenuOpen ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
            </svg>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden glass navbar-bar border-t border-border py-4 px-6 flex flex-col gap-4 animate-fade-in">
          {isAuthenticated ? (
            <>
              <Link to="/dashboard" onClick={() => setIsMobileMenuOpen(false)} className={dashboardLinkClass}>{t('nav.dashboard')}</Link>
              <Link to="/news" onClick={() => setIsMobileMenuOpen(false)} className={linkClass('/news')}>{t('nav.news')}</Link>
              <Link to="/invitations" onClick={() => setIsMobileMenuOpen(false)} className="relative">
                <span className={linkClass('/invitations')}>{t('nav.invitations')}</span>
                {unreadCount > 0 && <span className="ml-2 bg-destructive text-destructive-foreground text-small font-bold rounded-full h-5 w-5 inline-flex items-center justify-center">{unreadCount > 9 ? '9+' : unreadCount}</span>}
              </Link>
              <Link to="/account" onClick={() => setIsMobileMenuOpen(false)} className={linkClass('/account')}>{t('nav.account')}</Link>
              <Link to="/payments" onClick={() => setIsMobileMenuOpen(false)} className={linkClass('/payments')}>{t('nav.payments')}</Link>
              {isAdmin && (
                <Link to="/admin" onClick={() => setIsMobileMenuOpen(false)} className={adminLinkClass}>Admin</Link>
              )}
              <div className="border-t border-border pt-4">
                <div className="text-muted-foreground text-nav mb-2">Welcome, {user?.name}</div>
                <button onClick={handleLogout} className="text-muted-foreground hover:text-foreground text-nav w-full text-left">
                  {t('nav.signOut')}
                </button>
              </div>
            </>
          ) : (
            <>
              <Link to="/news" onClick={() => setIsMobileMenuOpen(false)} className={linkClass('/news')}>{t('nav.news')}</Link>
              <Link to="/company-registration" onClick={() => setIsMobileMenuOpen(false)} className={linkClass('/company-registration')}>{t('nav.companies')}</Link>
              <Link to="/arena" onClick={() => setIsMobileMenuOpen(false)} className={linkClass('/arena')}>{t('nav.arena')}</Link>
              <Link to="/golf" onClick={() => setIsMobileMenuOpen(false)} className={linkClass('/golf')}>{t('nav.golf')}</Link>
              <Link to="/showcase" onClick={() => setIsMobileMenuOpen(false)} className={linkClass('/showcase')}>{t('nav.predictions')}</Link>
              <Link to="/about-us" onClick={() => setIsMobileMenuOpen(false)} className={linkClass('/about-us')}>{t('nav.about')}</Link>
              <Link to="/login" onClick={() => setIsMobileMenuOpen(false)} className="px-5 py-2.5 rounded-lg bg-primary text-primary-foreground text-nav font-semibold text-center hover:opacity-90">
                {t('nav.signIn')}
              </Link>
            </>
          )}
        </div>
      )}
    </nav>
  )
}

export default Navigation
